import { useEffect } from 'react';

export default function ReferralProgram() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cash Bonus',
      description: 'Earn a generous cash bonus for every successful referral that results in a purchase'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Help Friends & Family',
      description: 'Give your network access to exceptional service and exclusive Mercedes deals'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Trusted Process',
      description: 'Your referrals receive the same transparent, pressure-free experience you did'
    },
    {
      icon: 'ri-infinite-line',
      title: 'Unlimited Referrals',
      description: 'No limit on how many people you can refer. The more referrals, the more bonuses'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Share My Contact',
      description: 'Tell your friends, family, or colleagues about your experience and share my contact information'
    },
    {
      number: '02',
      title: 'They Mention You',
      description: 'When they reach out, they simply mention your name as the referral source'
    },
    {
      number: '03',
      title: 'They Purchase',
      description: 'I help them find their perfect Mercedes with the same care and attention you received'
    },
    {
      number: '04',
      title: 'You Get Paid',
      description: 'Once the sale is complete, you receive your cash bonus. It\'s that simple'
    }
  ];

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e0453b9f0eb34aaf76dc6915674a267b.jpg"
            alt="Referral Program"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="w-24 h-24 bg-amber-400/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm">
            <i className="ri-gift-line text-amber-400 text-5xl"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Refer a Buyer. <span className="text-amber-400">Earn a Bonus.</span>
          </h1>
          <p className="text-gray-300 text-xl md:text-2xl max-w-3xl mx-auto mb-8 leading-relaxed">
            Love your Mercedes experience? Share it with others and get rewarded. It's my way of saying thank you for trusting me with your network.
          </p>
          <a 
            href="#contact"
            className="inline-block px-10 py-4 bg-amber-400 text-black font-semibold rounded-lg hover:bg-amber-500 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-lg"
          >
            <i className="ri-phone-line mr-2"></i>
            Contact Me to Learn More
          </a>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why <span className="text-amber-400">Refer?</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              It's a win-win. You earn a bonus, and your referral gets exceptional service
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-zinc-900 p-8 rounded-lg text-center hover:bg-zinc-800 transition-all duration-300 hover:scale-105"
              >
                <div className="w-16 h-16 bg-amber-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-amber-400 text-3xl`}></i>
                </div>
                <h3 className="text-white text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-gray-400 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-zinc-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              How It <span className="text-amber-400">Works</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Four simple steps to earn your referral bonus
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="bg-black p-8 rounded-lg relative overflow-hidden group hover:bg-zinc-950 transition-all duration-300"
              >
                <div className="absolute top-0 right-0 text-9xl font-bold text-amber-400/5 group-hover:text-amber-400/10 transition-colors">
                  {step.number}
                </div>
                <div className="relative z-10">
                  <div className="text-amber-400 text-2xl font-bold mb-4">{step.number}</div>
                  <h3 className="text-white text-2xl font-semibold mb-4">{step.title}</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-zinc-900 rounded-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Program <span className="text-amber-400">Details</span>
            </h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p className="flex items-start">
                <i className="ri-checkbox-circle-fill text-amber-400 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span>Cash bonus paid upon successful completion of vehicle purchase by your referral</span>
              </p>
              <p className="flex items-start">
                <i className="ri-checkbox-circle-fill text-amber-400 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span>Referral must mention your name during initial contact or consultation</span>
              </p>
              <p className="flex items-start">
                <i className="ri-checkbox-circle-fill text-amber-400 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span>No limit on the number of referrals you can make</span>
              </p>
              <p className="flex items-start">
                <i className="ri-checkbox-circle-fill text-amber-400 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span>Bonus amount varies based on vehicle type and purchase details</span>
              </p>
              <p className="flex items-start">
                <i className="ri-checkbox-circle-fill text-amber-400 text-xl mr-3 mt-1 flex-shrink-0"></i>
                <span>Contact me directly for specific bonus amounts and additional program details</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact" className="py-24 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-amber-400 to-amber-500 rounded-lg p-8 md:p-12 text-center">
            <h3 className="text-black text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Referring?
            </h3>
            <p className="text-black/80 text-lg mb-8 max-w-2xl mx-auto">
              Have questions about the referral program? Want to know the exact bonus amount? Let's talk.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:+18479573340" 
                className="px-10 py-4 bg-black text-white font-semibold rounded-lg hover:bg-zinc-900 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-lg"
              >
                <i className="ri-phone-line mr-2"></i>
                Call: (847) 957-3340
              </a>
              <a 
                href="mailto:jacob@mercedeswithjacob.com" 
                className="px-10 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-lg"
              >
                <i className="ri-mail-line mr-2"></i>
                Email Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black border-t border-zinc-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <a 
              href="/"
              className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Home
            </a>
            <p className="text-gray-500 text-sm text-center">
              © 2024 Mercedes with Jacob. All rights reserved. | 
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-500 ml-1 cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
